/**
 * @module app.um
 */

import { autoinject, bindingMode, TaskQueue } from 'aurelia-framework';
import { ComponentBase, Sorting, SortDirection } from 'app/shared/lib/essette-site';
import { bindable } from 'aurelia-typed-observable-plugin';
import { apiCall } from 'app/shared/lib/api/api';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { IMemberSearchFilters } from 'app/cd/services/member-service';
import { IAuthSearchFilters, AuthService } from 'app/um/services/auth-service'
import { SelectEvent, EssTableCustomElement } from 'app/shared/elements/ess-table';
import { AuthWithLocalState } from 'app/um/models/local-state-extensions';
import { AssignAuthsPageContext } from 'app/um/lib/assign-auths-context';
import { fireEvent } from 'app/shared/lib/fire-events';
import { openAuthSummary } from 'app/um/components/summary/auth-summary';
import { openProviderSummary } from 'app/cd/components/summary/provider/provider-summary';
import { guid } from 'app/shared/lib/guid';
import * as UM from 'app/um/models/essette-api';
import { requiredFields as memberRequiredFields, requiredPlanFields as planRequiredFields } from 'app/cd/elements/members/member-display';


interface IAuthSelectCustomEvent { ownerId: string, auths: ReadonlyArray<UM.Auth> }
export type AuthSelectCustomEvent = CustomEvent<IAuthSelectCustomEvent>;

interface IOpenAuthQueuesCustomEvent { auth: UM.Auth }
export type OpenAuthQueuesCustomEvent = CustomEvent<IOpenAuthQueuesCustomEvent>;

@autoinject()
export class AuthOwnerAuthsGrid extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable.stringNull({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    public ownerId: string | null = null;

    @bindable.stringNull({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    public ownerDisplay: string | null = null;

    @bindable.arrayNull({ defaultBindingMode: bindingMode.toView })
    public auths: ReadonlyArray<AuthWithLocalState> | null = null;

    private sorting: Sorting = new Sorting();
    private defaultSorting: Sorting = new Sorting();
    protected resultCount: number | null = 0;
    @bindable.number({ defaultBindingMode: bindingMode.toView })
    protected pageSize: number = 20;
    protected id: guid = guid();

    protected showAuthSummaryLink: boolean = false;
    protected openAuthSummary = openAuthSummary;
    protected showProviderSummaryLink: boolean = false;
    protected hasAuthRoute = false;
    protected openProviderSummary = openProviderSummary;

    ////****************** Element References ***************************************////
    protected $table!: EssTableCustomElement<AuthWithLocalState>;
    protected $pager!: EssPagerCustomElement;


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly $element: Element,
        private readonly context: AssignAuthsPageContext,
        private readonly service: AuthService,
        private readonly queue: TaskQueue,
        ...rest: any[]) {

        super(...rest);

        // Default sort
        this.defaultSorting.entries = [
            {
                reference: 'metadata.modified.timestamp',
                direction: SortDirection.Ascending
            },
            {
                reference: 'number',
                direction: SortDirection.Ascending
            }
        ];
    }

    protected bind(bindingContext: Object, overrideContext: Object): void {
        this.reload();
    }

    protected attached(): void { }

    protected detached(): void {
        // Empty string represents unassigned auths and is a valid ownerId
        if(this.ownerId || this.ownerId == "") { this.context.clearSelectedAuths(this.ownerId); }
        this.$table.selected.splice(0);
    }


    ////****************** Events ***************************************************////
    protected authsSelected(event: SelectEvent<UM.Auth>): void {
        // Empty string represents unassigned auths and is a valid ownerId
        if(!this.ownerId && this.ownerId !== '') { return; }
        const detail: IAuthSelectCustomEvent = { ownerId: this.ownerId, auths: event.detail.items }
        fireEvent(this.$element, 'auth-select', detail);
    }

    /** Handles the grid's sort event */
    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        this.reload();
    }


    ////****************** Functions ************************************************////
    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.showAuthSummaryLink = this.accountContext.hasRouteAccess('cm.case.summary');
        this.showProviderSummaryLink = this.accountContext.hasRouteAccess('cd.provider.summary');
        this.hasAuthRoute = this.accountContext.hasRouteAccess('um.auth');
    }

    public async reload(): Promise<void> {
        this.checkPermissions();

        if(this.ownerId || this.ownerId === '') {
            this.loadOwnerAuths(this.ownerId);
        }
    }

    protected openQueueHistory(auth: UM.Auth): void {
        if(this.$element && !!auth && !!auth.id) {
            const detail: IOpenAuthQueuesCustomEvent = { auth: auth };
            fireEvent(this.$element, 'open-queue', detail);
        }
    }

    private restoreSelected() {
        // Empty string represents unassigned auths and is a valid ownerId
        if(!this.ownerId && this.ownerId !== '') { return; }
        const selectedAuths: AuthWithLocalState[] = this.context.getSelectedAuths(this.ownerId);
        if(!(selectedAuths && selectedAuths.length > 0)) { return; }
        // Give aurelia a chance to bind data to the table before we ask the table to restore selected items
        this.queue.queueTask(() => {
            this.$table.restoreSelected(selectedAuths);
        });
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadOwnerAuths(ownerId: string | null): Promise<void> {
        const authFilters: IAuthSearchFilters = { assignedTo: { id: ownerId }, openOnly: true };
        const memberFilters: IMemberSearchFilters = {};

        const options: Partial<IQueryLoadOptions> = {
            top: this.pageSize,
            page: this.$pager?.page || 0,
            sorting: this.sorting.entries.length ? this.sorting : this.defaultSorting,
            includeInactive: true
        };

        const includes: string[] = [
            'assignedTo',
            'class',
            'class.subClass',
            'healthcareServiceType',
            'member',
            'member.plan',
            'member.plan.healthPlan',
            'member.plan.lineOfBusiness',
            'provider.servicing',
            'provider.servicing.location.provider',
            'placeOfService',
            'queues',
            'status',
            'status.reason',
            'type',
        ];

        const fields: string[] = [
            'class.description',
            'class.subClass.description',
            'dueDateTime',
            'healthcareServiceType.description',
            'metadata.modified.timestamp',
            'nextReviewDate',
            'number',
            'placeOfService.description',
            'portalQueue.id',
            'provider.servicing.location.provider.description',
            'provider.servicing.location.provider.name.full',
            'queues.description',
            'status.description',
            'type.description',
        ]

        memberRequiredFields.forEach(field => !fields.includes(`member.${field}`) ? fields.push(`member.${field}`) : null);
        planRequiredFields.forEach(field => !fields.includes(`member.plan.${field}`) ? fields.push(`member.plan.${field}`) : null);
        try {
            this.state.beginLoad();
            const response = await this.service.searchAuths(authFilters, memberFilters, options, includes, fields);
            this.auths = response.results;

            // this is needed for member-filter assignment validation
            fireEvent(this.$element, 'auths-loaded', { ownerId: this.ownerId, auths: this.auths });

            this.resultCount = response.count || 0;
            this.restoreSelected();
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }
}
